import { allocateHours } from "./allocate-hours.js";
import {
  getActiveRule,
  resolveStackedMultiplier,
  roundHours,
  roundMoney,
  ruleMultiplier,
} from "./helpers.js";
import type {
  CalculatePayInput,
  CalculationBreakdownLine,
  PayCalculationResult,
  PayRule,
  TimeEntryFlags,
} from "./types.js";

function modifierMultipliers(
  rules: PayRule[],
  flags: TimeEntryFlags,
): { value: number; priority?: number }[] {
  const modifiers: { value: number; priority?: number }[] = [];

  if (flags.isWeekend) {
    const weekend = getActiveRule(rules, "WEEKEND");
    if (weekend?.multiplier) {
      modifiers.push({ value: weekend.multiplier, priority: weekend.priority });
    }
  }

  if (flags.isHoliday) {
    const holiday = getActiveRule(rules, "HOLIDAY");
    if (holiday?.multiplier) {
      modifiers.push({ value: holiday.multiplier, priority: holiday.priority });
    }
  }

  if (flags.isTraining) {
    const training = getActiveRule(rules, "TRAINING");
    if (training?.multiplier) {
      modifiers.push({ value: training.multiplier, priority: training.priority });
    }
  }

  return modifiers;
}

function effectiveTierMultiplier(
  tierMultiplier: number,
  profile: CalculatePayInput["profile"],
  modifiers: { value: number; priority?: number }[],
): number {
  return resolveStackedMultiplier(profile.stackingMode, [
    { value: tierMultiplier },
    ...modifiers,
  ]);
}

function calculatePremiums(
  input: CalculatePayInput,
  hoursWorked: number,
  breakdown: CalculationBreakdownLine[],
): { premiumPay: number; premiumHours: number } {
  const { baseHourlyRate, flags, rules } = input;
  let premiumPay = 0;

  if (flags.isNightShift) {
    const nightRule = getActiveRule(rules, "NIGHT_SHIFT");
    if (nightRule?.percentagePremium) {
      const amount = roundMoney(hoursWorked * baseHourlyRate * nightRule.percentagePremium);
      premiumPay += amount;
      breakdown.push({
        category: "premium_night",
        hours: hoursWorked,
        rate: baseHourlyRate,
        multiplier: 1 + nightRule.percentagePremium,
        amount,
        description: `Night shift premium (${(nightRule.percentagePremium * 100).toFixed(0)}%)`,
      });
    }
  }

  if (flags.isHazard) {
    const hazardRule = getActiveRule(rules, "HAZARD");
    if (hazardRule?.flatAmount) {
      const amount = roundMoney(hoursWorked * hazardRule.flatAmount);
      premiumPay += amount;
      breakdown.push({
        category: "premium_hazard",
        hours: hoursWorked,
        rate: hazardRule.flatAmount,
        multiplier: 1,
        amount,
        description: `Hazard premium ($${hazardRule.flatAmount.toFixed(2)}/hr)`,
      });
    }
  }

  if (flags.isTraining) {
    const trainingRule = getActiveRule(rules, "TRAINING");
    if (trainingRule?.flatAmount) {
      const amount = roundMoney(hoursWorked * trainingRule.flatAmount);
      premiumPay += amount;
      breakdown.push({
        category: "premium_training",
        hours: hoursWorked,
        rate: trainingRule.flatAmount,
        multiplier: 1,
        amount,
        description: `Training premium ($${trainingRule.flatAmount.toFixed(2)}/hr)`,
      });
    }
  }

  return { premiumPay, premiumHours: premiumPay > 0 ? hoursWorked : 0 };
}

function calculateEquipmentCost(
  equipmentUsage: CalculatePayInput["equipmentUsage"],
  breakdown: CalculationBreakdownLine[],
): number {
  if (!equipmentUsage?.length) return 0;

  let total = 0;
  for (const usage of equipmentUsage) {
    const amount = roundMoney(usage.usageHours * usage.hourlyCost);
    total += amount;
    breakdown.push({
      category: "equipment",
      hours: usage.usageHours,
      rate: usage.hourlyCost,
      multiplier: 1,
      amount,
      description: usage.equipmentId
        ? `Equipment ${usage.equipmentId}`
        : "Equipment usage",
    });
  }

  return roundMoney(total);
}

export function calculatePay(input: CalculatePayInput): PayCalculationResult {
  const warnings: string[] = [];
  const breakdown: CalculationBreakdownLine[] = [];

  const {
    baseHourlyRate,
    hoursWorked,
    cumulativeWeeklyHoursBefore,
    flags,
    profile,
    rules,
    equipmentUsage,
  } = input;

  if (hoursWorked < 0) {
    warnings.push("Negative hours worked were clamped to zero.");
  }
  if (baseHourlyRate < 0) {
    warnings.push("Negative base hourly rate may produce invalid pay.");
  }

  const hours = Math.max(0, hoursWorked);
  const activeRules = rules.filter((rule) => rule.active !== false);
  if (activeRules.length === 0) {
    warnings.push("No active pay rules; using straight-time pay only.");
  }

  const buckets = allocateHours(hours, cumulativeWeeklyHoursBefore, activeRules, flags);
  const modifiers = modifierMultipliers(activeRules, flags);

  const dailyOtRule = getActiveRule(activeRules, "DAILY_OT");
  const dailyDtRule = getActiveRule(activeRules, "DAILY_DT");
  const holidayRule = getActiveRule(activeRules, "HOLIDAY");

  const regularTierMult = effectiveTierMultiplier(1, profile, modifiers);
  const overtimeTierMult = effectiveTierMultiplier(
    ruleMultiplier(dailyOtRule, 1.5),
    profile,
    modifiers,
  );
  const doubleTimeTierMult = effectiveTierMultiplier(
    ruleMultiplier(dailyDtRule, 2),
    profile,
    modifiers,
  );
  const holidayTierMult = effectiveTierMultiplier(
    ruleMultiplier(holidayRule, 2),
    profile,
    modifiers,
  );

  let regularPay = 0;
  let overtimePay = 0;
  let doubleTimePay = 0;

  if (buckets.holiday > 0) {
    const amount = roundMoney(buckets.holiday * baseHourlyRate * holidayTierMult);
    regularPay += amount;
    breakdown.push({
      category: "holiday",
      hours: buckets.holiday,
      rate: baseHourlyRate,
      multiplier: holidayTierMult,
      amount,
      description: `Holiday pay (${holidayTierMult}x)`,
    });
  }

  if (buckets.regular > 0) {
    const amount = roundMoney(buckets.regular * baseHourlyRate * regularTierMult);
    regularPay += amount;
    breakdown.push({
      category: "regular",
      hours: buckets.regular,
      rate: baseHourlyRate,
      multiplier: regularTierMult,
      amount,
      description: `Regular pay (${regularTierMult}x)`,
    });
  }

  if (buckets.overtime > 0) {
    const amount = roundMoney(buckets.overtime * baseHourlyRate * overtimeTierMult);
    overtimePay += amount;
    breakdown.push({
      category: "overtime",
      hours: buckets.overtime,
      rate: baseHourlyRate,
      multiplier: overtimeTierMult,
      amount,
      description: `Overtime pay (${overtimeTierMult}x)`,
    });
  }

  if (buckets.doubleTime > 0) {
    const amount = roundMoney(buckets.doubleTime * baseHourlyRate * doubleTimeTierMult);
    doubleTimePay += amount;
    breakdown.push({
      category: "double_time",
      hours: buckets.doubleTime,
      rate: baseHourlyRate,
      multiplier: doubleTimeTierMult,
      amount,
      description: `Double-time pay (${doubleTimeTierMult}x)`,
    });
  }

  const { premiumPay, premiumHours } = calculatePremiums(input, hours, breakdown);
  const equipmentCost = calculateEquipmentCost(equipmentUsage, breakdown);

  regularPay = roundMoney(regularPay);
  overtimePay = roundMoney(overtimePay);
  doubleTimePay = roundMoney(doubleTimePay);
  const grossPay = roundMoney(regularPay + overtimePay + doubleTimePay + premiumPay);

  return {
    regularHours: roundHours(buckets.regular),
    overtimeHours: roundHours(buckets.overtime),
    doubleTimeHours: roundHours(buckets.doubleTime),
    holidayHours: roundHours(buckets.holiday),
    premiumHours: roundHours(premiumHours),
    regularPay,
    overtimePay,
    doubleTimePay,
    premiumPay: roundMoney(premiumPay),
    equipmentCost,
    grossPay,
    calculationBreakdown: breakdown,
    warnings,
  };
}
